import { colors } from "../../colors";
import type {
  NotificationMessageSize,
  NotificationMessageStyleProps,
  NotificationMessageVariant,
} from "./types";

// Default colors for notification variants
const DEFAULT_COLORS = {
  success: {
    background: colors.cyan[50], // Light background for success
    color: colors.cyan[600], // Dark text for contrast
    icon: colors.cyan[600],
  },
  error: {
    background: colors.primary[50], // Light red background
    color: colors.primary[600], // Dark text for contrast
    icon: colors.error,
  },
  warning: {
    background: colors.gray[100], // Light background for warning
    color: colors.gray[800], // Dark text for contrast
    icon: colors.warning,
  },
  info: {
    background: colors.primary[50], // Light primary background
    color: colors.primary[300], // Darker primary for better contrast
    icon: colors.info,
  },
};

// Default size styles
export const getNotificationMessageSizeStyles = (
  size: NotificationMessageSize,
): string => {
  switch (size) {
    case "small":
      return `
        padding: 12px;
        font-size: 0.9em;
        line-height: 20px;
        gap: 8px;
        
        .notification-title {
          font-size: 0.9em;
          margin-bottom: 4px;
        }
        
        .notification-icon {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }
        
        .notification-dismiss {
          width: 16px;
          height: 16px;
          padding: 2px;
        }
      `;
    case "large":
      return `
        padding: 20px;
        font-size: 1.2em;
        line-height: 24px;
        gap: 14px;
        
        .notification-title {
          font-size: 1.2em;
          margin-bottom: 8px;
        }
        
        .notification-icon {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }
        
        .notification-dismiss {
          width: 20px;
          height: 20px;
          padding: 4px;
        }
      `;
    case "medium":
    default:
      return `
        padding: 16px;
        font-size: 1em;
        line-height: 22px;
        gap: 12px;
        
        .notification-title {
          font-size: 1em;
          margin-bottom: 6px;
        }
        
        .notification-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }
        
        .notification-dismiss {
          width: 18px;
          height: 18px;
          padding: 3px;
        }
      `;
  }
};

// Default variant styles
export const getNotificationMessageVariantStyles = (
  variant: NotificationMessageVariant,
  styleProps?: NotificationMessageStyleProps,
): string => {
  const variantColors = DEFAULT_COLORS[variant];
  const { backgroundColor, color, borderColor, iconColor, boxShadow } =
    styleProps ?? {};

  return `
    background-color: ${backgroundColor ?? variantColors.background};
    ${borderColor ? `border: 1px solid ${borderColor};` : ""};
    color: ${color ?? variantColors.color};
     ${boxShadow ? `box-shadow:${boxShadow};` : ""};
    
    .notification-icon {
      color: ${iconColor ?? variantColors.icon};
    }
    
    .notification-dismiss {
      color: ${color ?? variantColors.color};
      opacity: 0.5;
      
      &:hover {
        opacity: 1;
        transform: scale(1.1);
      }
    }
  `;
};

// Icon size styles
export const getNotificationIconSizeStyles = (
  size: NotificationMessageSize,
  styleProps?: NotificationMessageStyleProps,
): string => {
  const sizeStyles = getNotificationMessageSizeStyles(size);

  // Extract icon size from the size styles
  const iconSizeMatch = sizeStyles.match(
    /\.notification-icon\s*{\s*width:\s*([^;]+);/,
  );
  const iconSize = iconSizeMatch?.[1]?.trim() ?? "20px";

  return `
    width: ${iconSize};
    height: ${iconSize};
    flex-shrink: 0;
    color: ${styleProps?.iconColor ?? "inherit"};
  `;
};

// Dismiss button styles
export const getNotificationDismissButtonStyles = (
  size: NotificationMessageSize,
  variant: NotificationMessageVariant,
): string => {
  const variantColors = DEFAULT_COLORS[variant];
  const sizeStyles = getNotificationMessageSizeStyles(size);

  // Extract dismiss button size from the size styles
  const dismissSizeMatch = sizeStyles.match(
    /\.notification-dismiss\s*{\s*width:\s*([^;]+);.*?height:\s*([^;]+);.*?padding:\s*([^;]+);/s,
  );
  const dismissWidth = dismissSizeMatch?.[1]?.trim() ?? "18px";
  const dismissHeight = dismissSizeMatch?.[2]?.trim() ?? "18px";
  const dismissPadding = dismissSizeMatch?.[3]?.trim() ?? "3px";

  return `
    width: ${dismissWidth};
    height: ${dismissHeight};
    padding: ${dismissPadding};
    margin-left: auto;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: ${variantColors.color};
    opacity: 0.7;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease-in-out;
    flex-shrink: 0;
    
    &:hover {
      opacity: 1;
    }
    
    &:active {
      background-color: ${colors.gray[300]};
    }
    
    &:focus {
      outline: none;
    }
    
    svg {
      width: 100%;
      height: 100%;
    }
  `;
};
